import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import styles from './App.module.css';
import { Catalog } from './components/Catalog';
import { Code } from './components/Code';
import { contentPosition, theme, themeColors } from './components/custom/Theme';
import { Error } from './components/Error';
import { ExperimentalFeatures } from './components/experimantal/ExperimentalFeatures';
import { Footer } from './components/Footer';
import { Login } from './components/Login';
import { NotificationPanel } from './components/notice/NotificationPanel';
import { AdminRoute, getAdminRoutes } from './components/routes/AdminRoute';
import { CustomerRoute, getCustomerRoutes } from './components/routes/CustomerRoute';
import { getSystemRoutes, OwnerOrEditorRoute } from './components/routes/OwnerOrEditorRoute';
import { getPrivateRoutes, PrivateRoute } from './components/routes/PrivateRoute';
import { PublicRoute } from './components/routes/PublicRoute';
import { TopBar } from './components/TopBar';
import { CLIENT_ID } from './constants';
import { Roles, routes, tabs } from './enums';
import { setClientProfile, setSelectedTheme, setStartRoutePath, TAppSlice } from './redux/appSlice';
import { TNoticesSlice } from './redux/noticesSlice';
import { useGetClientInfoQuery } from './redux/services/clients';
import { useGetMeInfoQuery, useGetUserRolesQuery } from './redux/services/users';
import { RootState } from './redux/store';
import {
  setIsAuthorized,
  setUserOrgId,
  setUserProfile,
  setUserRoleInApp,
  setUserRoles,
  TUserSlice,
} from './redux/userSlice';
import { checkAccessToken } from './requests/oidc';
import { getFirstClientInfo } from './requests/user';
import { getAccessToken } from './service/auth';
import { getImageURL } from './helpers';

type TAppProps = {
  isNotificationPanelOpen: TAppSlice['isNotificationPanelOpen'];
  isSnackbarOpen: TAppSlice['isSnackbarOpen'];
  notices?: TNoticesSlice['notices'];
  orgId?: TUserSlice['orgId'];
};

const mapStateToProps = (state: RootState) => ({
  isNotificationPanelOpen: state.app.isNotificationPanelOpen,
  isSnackbarOpen: state.app.isSnackbarOpen,
  notices: state.notices.notices,
  orgId: state.user.orgId,
});

const AppComponent: FC<TAppProps> = ({
  isNotificationPanelOpen,
  isSnackbarOpen,
  notices,
  orgId,
}) => {
  const dispatch = useDispatch();
  const { selectedTheme } = useSelector((state: RootState) => state.app);
  const { enqueueSnackbar } = useSnackbar();
  const [isAccessToken, setIsAccessToken] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: profile } = useGetMeInfoQuery(undefined, {
    skip: !isAccessToken,
  });
  const { data: client } = useGetClientInfoQuery(
    { id: CLIENT_ID },
    {
      skip: !isAccessToken,
    },
  );
  const { data: roles } = useGetUserRolesQuery({ id: profile?.id || '' }, { skip: !profile?.id });

  useLayoutEffect(() => {
    const getToken = async () => {
      try {
        setIsCheckingAuth(true);
        const token = await getAccessToken();
        const checkResult = token ? await checkAccessToken(token) : false;
        setIsAccessToken(checkResult);
        dispatch(setIsAuthorized(checkResult));
      } catch (err) {
        console.error('getToken error:', err);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    getToken();
  }, []);

  useEffect(() => {
    const theme = localStorage.getItem('theme');
    if (theme) dispatch(setSelectedTheme(theme as TAppSlice['selectedTheme']));
  }, []);

  useEffect(() => {
    const getProfile = async () => {
      if (profile?.id) {
        const roleInRootApp = profile?.Role?.[0]?.role;
        if (roleInRootApp) {
          dispatch(setUserRoleInApp(roleInRootApp));
        }
        dispatch(setUserProfile(profile));
      }
    };

    getProfile();
  }, [profile]);

  useEffect(() => {
    const getProfile = async () => {
      const fetchedClient = await getFirstClientInfo();
      if (fetchedClient) {
        dispatch(setClientProfile(fetchedClient));
      }
    };

    getProfile();

    //Отображение favicon
    if (client?.avatar) {
      const url = getImageURL(client?.avatar);
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      if (url) link.href = url;
    }
  }, [client]);

  useEffect(() => {
    if (roles?.length) {
      dispatch(setUserRoles(roles));
      roles.forEach((item) => {
        if (item.role === Roles.OWNER && item.client.parent_id === null) {
          dispatch(setUserOrgId(item.client.client_id));
        }
      });
    }
  }, [roles]);

  useEffect(() => {
    if (!isNotificationPanelOpen && !isSnackbarOpen) {
      const firstItem = notices?.[0];
      if (typeof firstItem?.message === 'string')
        enqueueSnackbar(firstItem?.message, {
          variant: 'customSnackbar',
          snackbarVariant: firstItem.type,
        });
    }
  }, [notices?.length]);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    rootElement?.classList.add('animate-color');
    document.documentElement.setAttribute('transition', 'background-color, color 0.4s ease');
    document.documentElement.style.setProperty(
      '--main-background-color',
      themeColors[selectedTheme].backgroundColor,
    );
    document.documentElement.style.setProperty(
      '--selected-background-color',
      themeColors[selectedTheme].backgroundSelectedColor,
    );
    document.documentElement.style.setProperty(
      '--background-disabled-color',
      themeColors[selectedTheme].backgroundDisabledColor,
    );
    document.documentElement.style.setProperty(
      '--background-darkened-color',
      themeColors[selectedTheme].backgroundDarkenedColor,
    );
    document.documentElement.style.setProperty(
      '--hover-background-color',
      themeColors[selectedTheme].backgroundHoverColor,
    );
    document.documentElement.style.setProperty(
      '--button-background-color',
      themeColors[selectedTheme].buttonBackgroundColor,
    );
    document.documentElement.style.setProperty(
      '--main-color',
      themeColors[selectedTheme].mainColor,
    );
    document.documentElement.style.setProperty(
      '--main-grey-color',
      themeColors[selectedTheme].mainGreyColor,
    );
    document.documentElement.style.setProperty(
      '--link-color',
      themeColors[selectedTheme].linkColor,
    );
    document.documentElement.style.setProperty('--accent-color', themeColors[selectedTheme].accent);
    document.documentElement.style.setProperty(
      '--on-accent-color',
      themeColors[selectedTheme].onAccentColor,
    );
    document.documentElement.style.setProperty('--content-position', contentPosition);

    setTimeout(() => rootElement?.classList.remove('animate-color'), 300);
  }, [selectedTheme]);

  return (
    <ThemeProvider theme={theme(themeColors[selectedTheme])}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <BrowserRouter>
          <AppRoutes
            isAccessToken={isAccessToken}
            orgId={orgId}
            contentRef={contentRef}
            isCheckingAuth={isCheckingAuth}
          />
        </BrowserRouter>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

const AppRoutes: FC<{
  isAccessToken: boolean;
  orgId?: string;
  isCheckingAuth: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
}> = ({ isAccessToken, orgId, isCheckingAuth, contentRef }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/code';

  if (isCheckingAuth) {
    return <></>;
  }

  if (!isAccessToken && !isAuthPage) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <>
      <TopBar />
      <Box className={styles.wrapper} ref={contentRef}>
        <Catalog />
        <Box className={styles.content}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/code" element={<Code />} />
            <Route path="/experimental" element={<ExperimentalFeatures />} />

            <Route element={<PublicRoute />}>
              <Route index path="/error/:text" element={<Error />} />
            </Route>

            <Route path={routes.profile} element={<PrivateRoute />}>
              {getPrivateRoutes()}
            </Route>

            <Route path={routes.admin} element={<AdminRoute />}>
              {getAdminRoutes(orgId || CLIENT_ID)}
            </Route>

            <Route path={routes.customer} element={<CustomerRoute />}>
              {getCustomerRoutes(orgId)}
            </Route>

            <Route path={routes.system} element={<OwnerOrEditorRoute />}>
              {getSystemRoutes()}
            </Route>

            <Route
              path="*"
              element={<Navigate to={`/${routes.profile}/${tabs.profile}`} replace />}
            />
          </Routes>
        </Box>
        <NotificationPanel />
      </Box>
      <Footer />
    </>
  );
};

export const App = connect(mapStateToProps)(AppComponent);
